import type { CoverageSchemaResponse } from '@/features/quotes/types/group-life/coverage-schema.types';
import type {
  BusinessUnderwritingMessage,
  BusinessUnderwritingMessagesResponse,
} from '@/services/quotes/types';
import { authenticationService } from '../authentication';
import { BaseService } from '../base';

export type GroupLifeTemplateDownload = {
  url: string;
  filename: string;
  expiresIn: number;
};

export type GroupLifeQuotePdfResponse = {
  url: string;
  key: string;
};

export type GroupLifeMembersExport = {
  url: string;
  filename: string;
  total: number;
};

export type GroupLifeParsedMember = {
  rowNumber: number;
  /** Identidade estável atribuída no back — âncora do diff incremental. */
  id?: string;
  cpf: string;
  name: string;
  dateOfBirth: string;
  gender: 'M' | 'F' | '';
  insurableGroup: string;
  capital: string;
  awayFromWork: boolean;
  cids: string[];
};

export type GroupLifeParseRowError = {
  rowNumber: number;
  column: string;
  message: string;
};

export type GroupLifeMembersInsuremoSync = {
  status: 'SENT' | 'SKIPPED' | 'FAILED';
  sentCount: number;
  skippedDuplicates?: number;
  error?: string;
};

export type GroupLifeMembersImportResponse = {
  importId: string;
  quoteId: string;
  targetId: string;
  filename: string;
  size: number;
  uploadedAt: string;
  status: 'PARSED' | 'PARSED_WITH_ERRORS' | 'PARSE_FAILED';
  totalRowsRead: number;
  members: GroupLifeParsedMember[];
  errors: GroupLifeParseRowError[];
  insuremoSync?: GroupLifeMembersInsuremoSync;
};

/** Resposta inicial (202) do upload ASSÍNCRONO via Upload Framework do InsureMO. */
export type GroupLifeDuplicateCpfsPayload = {
  totalCount: number;
  alreadyInPolicy: { cpf: string; rawCpf: string; rowNumber: number }[];
  repeatedInBatch: { cpf: string; rawCpf: string; rowNumbers: number[] }[];
};

export type GroupLifeAsyncImportStartResponse = {
  importId: string;
  taskId: string;
  status: 'PARSING' | 'FAILED';
  /** Quantidade de vidas válidas detectadas no parsing local — usada para
   *  detectar término do batch (countMembers no InsureMO ≥ totalExpected). */
  totalExpected: number;
  /** Preview imediato para o front popular a tabela enquanto o batch processa. */
  members: GroupLifeParsedMember[];
  /** Erros do parsing LOCAL (formato do Excel) — não confundir com erros do
   *  Upload Framework (esses vêm depois em `getAsyncImportStatus`). */
  parseErrors: GroupLifeParseRowError[];
};

export type GroupLifeAsyncImportUploadError = {
  rowNumber?: number;
  field?: string;
  message: string;
};

/** Resposta do polling de status. Quando `status === 'COMPLETED'`, `members`
 *  traz as vidas materializadas com seus `PolicyElementId` (identidade estável
 *  da vida no InsureMO). */
export type GroupLifeMembersRatingProgress = {
  total: number;
  rated: number;
  pending: number;
  status?: 'running' | 'failed';
  error?: string;
};

export type GroupLifeAsyncImportStatusResponse = {
  importId: string;
  status: 'PARSING' | 'HYDRATING' | 'COMPLETED' | 'UPDATE' | 'FAILED';
  totalExpected: number;
  totalCreated: number;
  errorCount: number;
  errors?: GroupLifeAsyncImportUploadError[];
  members?: GroupLifeFrontMember[];
  completedAt?: string;
};

/** Payload de entrada para CRUD individual de vida (add/edit). */
export type GroupLifeMemberCrudInput = {
  /**
   * Id do subestipulante destino (mesmo formato publicado pelo back e usado no
   * front: `RiskGroupId || StipulantRef || sub-<idx>`). Ausente OU literal
   * `__STIPULATOR__` ⇒ estipulante master. Permite ao back rotear a vida para
   * o PolicyRiskGroup correto.
   */
  targetId?: string;
  UniqueID?: string;
  cpf?: string | null;
  name: string;
  dateOfBirth: string;
  gender?: 'M' | 'F' | '';
  insurableGroup?: string;
  capital?: string;
  awayFromWork?: boolean;
  cids?: string[];
};

export type GroupLifeSubStipulatorCrudInput = {
  cnpj: string;
  legalName: string;
  cnaeCode?: string;
  email?: string;
  phone?: string;
  /** true → documento é CAEPF (IdType 3 no InsureMO); false/ausente → CNPJ. */
  isCaepf?: boolean;
  /** CAEPF: CPF consultado (o `cnpj` carrega o CAEPF). */
  cpf?: string;
};

export type GroupLifeSubStipulatorResponse = {
  id: string;
  sequenceNumber: number;
  riskGroupNo: number | string;
  cnpj: string;
  legalName: string;
  cnaeCode?: string;
  email?: string;
  phone?: string;
};

export type GroupLifeSubStipulatorDeleted = {
  deletedId: string;
  removedMembersCount: number;
};

/** Vida materializada — devolvida pelo back após add/edit individual. */
export type GroupLifeMemberCrudResponse = {
  id?: string;
  sequenceNumber?: number;
  policyElementId?: number | string;
  cpf: string;
  name: string;
  dateOfBirth: string;
  gender: 'M' | 'F' | '';
  insurableGroup: string;
  capital: string;
  awayFromWork: boolean;
  cids: string[];
};

/** Resposta paginada de vidas de um target (server-side). */
export type GroupLifeMembersPageResponse = {
  /** Total real do target no InsureMO — toda página até ele é alcançável. */
  total: number;
  pageNo: number;
  pageSize: number;
  members: GroupLifeMemberCrudResponse[];
};

export type GroupLifeStipulatorPayload = {
  isCaepf?: boolean;
  registerNumber?: string;
  /** CAEPF: CPF consultado (o `registerNumber` traz o CAEPF). */
  cpf?: string;
  responsibleName?: string;
  tradeName?: string;
  cnaeCode?: number | string;
  email?: string;
  phone?: string;
  insuranceType?: 'NEW' | 'RENEWAL' | 'TRANSFER';
  transferOriginInsurerCode?: string;
  /** Idades médias da Diretrix (quote-engine-data). */
  averagePartnerAge?: number;
  averageEmployeeAge?: number;
};

export type GroupLifeAgentPayload = {
  name?: string;
  agentCode?: string;
  partyCode?: string;
  advisoryId?: string;
  registerNumber?: string;
};

export type GroupLifeCreateQuotePayload = {
  /** Identidade do produto comercial — vem do registry de produtos (mesmo
   *  padrão do residencial). Campos técnicos são opcionais (InsureMO resolve
   *  a partir do productCode quando ausentes). */
  productId: number;
  productCode: string;
  productVersion?: string;
  productElementId?: number;
  techProductCode?: string;
  techProductId?: number;
  stipulator: GroupLifeStipulatorPayload;
  agent?: GroupLifeAgentPayload;
};

export type GroupLifeCreateQuoteResponse = {
  policyId: string;
  proposalNumber: string;
  productCode: string;
};

export type GroupLifeCopyQuoteResponse = {
  policyId: string;
  proposalNumber: string;
  productCode: string;
};

/**
 * Corpo do recálculo da PROPOSTA PME. O recálculo NÃO encaminha a proposta para
 * análise — quem decide é a subscrição do Confirmar. `forceMemberRecalculation`
 * re-tarifa vida a vida (lento) e só é necessário quando o PLANO mudou.
 */
export type GroupLifeCalculateProposalPayload = {
  forceMemberRecalculation?: boolean;
};

export type GroupLifeCalculateAnalysisPayload = {
  status?: string;
  brokeragePercent?: number;
  agencyingPercent?: number;
  surchargePercent?: number;
  actuarialDiscountPercent?: number;
};

export type GroupLifeSubmitAnalysisResponse = {
  policyId: string;
  statusPortal: string;
  quoteNo?: string;
};

export type GroupLifeRejectAnalysisResponse = {
  policyId: string;
  statusPortal: string;
  description: string;
  refusalLetterGenerated: boolean;
  refusalLetterKey?: string;
};

export type GroupLifeStepName =
  | 'stipulator'
  | 'plan'
  | 'risk'
  | 'commercialization'
  | 'calculation';

export type GroupLifeSaveStepResponse = {
  policyId: string;
  step: GroupLifeStepName;
  savedAt: string;
};

/**
 * Endereço do cliente — o back lê/grava nos campos planos do PolicyCustomer
 * (CEP/Endereco/EnderecoNumero/Bairro/Cidade/Estado).
 */
export type GroupLifeFrontCustomerAddress = {
  zipCode?: string;
  street?: string;
  number?: string;
  complement?: string;
  neighborhood?: string;
  city?: string;
  state?: string;
};

export type GroupLifeFrontStipulator = {
  isCaepf: boolean;
  registerNumber: string;
  address?: GroupLifeFrontCustomerAddress;
  /** CAEPF: CPF consultado (o `registerNumber` traz o CAEPF). */
  cpf?: string;
  /** CAEPF (pessoa física): sexo e data de nascimento do titular (proposta). */
  genderCode?: string;
  dateOfBirth?: string;
  responsibleName: string;
  tradeName?: string;
  cnaeCode?: string;
  email?: string;
  phone?: string;
  insuranceType?: '1' | '3' | '4';
  /** Código da congênere (policy.SeguradoraAnterior) quando Transferência. */
  transferOriginInsurerCode?: string;
  /** Idades médias da Diretrix (PolicyCustomer.AveragePartnerAge/
   *  AverageEmployeeAge). */
  averagePartnerAge?: number;
  averageEmployeeAge?: number;
};

export type GroupLifeFrontCoverageSelection =
  | { kind: 'DECLARED'; enabled: boolean; capital: number | null }
  | {
      kind: 'PROPORTIONAL_TO_COVERAGE';
      enabled: boolean;
      proportionOverride?: number;
    }
  | {
      kind: 'PROPORTIONAL_TO_RISK';
      enabled: boolean;
      proportionOverride?: number;
    }
  | {
      kind: 'BENEFIT_SELECTION';
      enabled: boolean;
      selectedChildCoverageCode: string | null;
    }
  | {
      /** Diárias (DH/DIT): opção escolhida + `capital` = VALOR da diária. */
      kind: 'BENEFIT_SELECTION_WITH_SI';
      enabled: boolean;
      selectedChildCoverageCode: string | null;
      capital: number | null;
    }
  | { kind: 'EXCLUSIVE_GROUP'; enabled: boolean };

export type GroupLifeFrontPlan = {
  modality?: 'UNIFORM' | 'VARIABLE' | 'MULTIPLE_SALARY';
  uniformCapital?: string;
  numberOfSalaries?: string;
  minimumIndividualCapital?: string;
  maximumIndividualCapital?: string;
  maximumDpsWaiverCapital?: string;
  /**
   * Capital segurado do ESTIPULANTE master por cobertura, tarifado pelo
   * InsureMO — já com o teto de cada cobertura e o limite individual do plano
   * aplicados. O de cada subestipulante vem em `subStipulators`. Só existe
   * depois do cálculo; use-o em vez de derivar o valor do percentual na tela.
   */
  coverageSumInsured?: Record<string, number>;
  coverages: Record<string, GroupLifeFrontCoverageSelection>;
};

export type GroupLifeFrontSubStipulator = {
  id: string;
  cnpj: string;
  address?: GroupLifeFrontCustomerAddress;
  legalName: string;
  cnaeCode?: string;
  email?: string;
  phone?: string;
  /** true → documento é CAEPF (pessoa física); false/ausente → CNPJ. */
  isCaepf?: boolean;
  /** CAEPF: CPF consultado (o `cnpj` carrega o CAEPF). */
  cpf?: string;
  /** CAEPF (pessoa física): sexo e data de nascimento do titular (proposta). */
  genderCode?: string;
  dateOfBirth?: string;
  /** Vidas particionadas pelo back via `subStipulatorId` no StoredMember. */
  members?: GroupLifeFrontMember[];
  /**
   * Capital segurado deste subestipulante por cobertura. O do plano
   * (`plan.coverageSumInsured`) é só o do estipulante master.
   */
  coverageSumInsured?: Record<string, number>;
};

export type GroupLifeFrontCoBroker = {
  agentCode: string;
  name: string;
  sharePercent: string;
  registerNumber?: string;
  susepCode?: string;
};

export type GroupLifeFrontCommercialization = {
  /** Corretagem. Ausente quando a cotação ainda não passou pelo step. */
  brokeragePercent?: string;
  leaderSharePercent?: string;
  coBrokers?: GroupLifeFrontCoBroker[];
  /**
   * Agenciamento. `agencyPercent` é o alias LEGADO do mesmo valor: payloads
   * antigos trazem um, os novos trazem o outro, nunca os dois — por isso ambos
   * são opcionais. Quem lê resolve com fallback (ver
   * `build-commercial-letter-payload`).
   */
  agencingPercent?: string;
  agencyPercent?: string;
  /** Agravo (PolicyDiscountLoadingList tipo "S") — string pt-BR ("10,00"). */
  surchargePercent?: string;
  /** Desconto atuarial (PolicyDiscountLoadingList tipo "D") — string pt-BR. */
  actuarialDiscountPercent?: string;
};

/** Vida lida do InsureMO (PolicyRiskList) na hidratação. */
export type GroupLifeFrontMember = {
  /** Identidade estável (uuid) — âncora do diff incremental do Step 3. */
  id?: string;
  cpf: string;
  name: string;
  dateOfBirth: string; // DD/MM/YYYY
  gender: 'M' | 'F' | '';
  insurableGroup: string;
  capital: string;
  awayFromWork: boolean;
  cids: string[];
};

/** Resultado do CÁLCULO da cotação (prêmio), lido do snapshot do InsureMO.
 *  É gravado pelo back ao salvar o Step 4 (Comercialização), que dispara
 *  `update → calculate → update` no InsureMO. Ausente quando o cálculo ainda
 *  não rodou (ex.: usuário não concluiu a Comercialização). */
/** Prêmio mensal publicado pelo back (`calculation.monthly`). */
export type GroupLifeMonthlyPremium = {
  total: number;
  iof: number;
  /** "Prêmio Líquido" das telas: total − IOF (não o netPremium atuarial). */
  beforeIof: number;
  source: 'INSTALLMENT' | 'ANNUAL_DIVIDED';
};

export type GroupLifeQuoteCalculation = {
  /** Prêmio líquido (PremioLiquido / NetPremium). */
  netPremium: number;
  /** IOF. */
  iof: number;
  /** Prêmio total ANUAL (GrossPremium / DuePremium). */
  totalPremium: number;
  /** Prêmio MENSAL total — espelho de `monthly.total`, mantido por
   *  compatibilidade. Prefira `resolveGroupLifeMonthlyPremium`. */
  monthlyPremium?: number;
  /** Prêmio MENSAL (total, IOF e líquido) calculado UMA vez pelo back — a
   *  mesma fonte dos PDFs. Leia via `resolveGroupLifeMonthlyPremium`. */
  monthly?: GroupLifeMonthlyPremium;
  /** Periodicidade de pagamento (ex.: "Mensal"). */
  frequency?: string;
  /** Validade da cotação no formato DD/MM/YYYY (ExpiryDate). */
  validity?: string;
  /** true quando o InsureMO confirmou o cálculo (IsPremiumCalcSuccess). */
  calculated: boolean;
  /** Demonstrativo por cobertura. `description` pode vir como o CÓDIGO da
   *  cobertura — o front resolve o nome amigável via coverageSchema. */
  breakdown: {
    description: string;
    value: number;
    iof: number;
    total: number;
  }[];
};

export type GroupLifeQuoteVersionItem = {
  quotationVersionId: number;
  versionName: string;
  versionDescription?: string;
  isSelected?: string;
  type: string;
  date: string;
  number: string;
  lives: number;
  removed: number;
  averageRate: number;
  totalPremium: number;
  /**
   * Procedência da versão numa proposta clonada: CURRENT = da própria
   * proposta; SOURCE = herdada de uma proposta de origem da cadeia de
   * clonagem. Ausente fora do contexto de clone.
   */
  origin?: 'SOURCE' | 'CURRENT';
  /** PolicyId da proposta de origem dona da versão (apenas itens SOURCE). */
  sourcePolicyId?: string;
};

export type ListGroupLifeQuoteVersionsResponse = {
  policyId: string;
  versions: GroupLifeQuoteVersionItem[];
};

/** Um ancestral na cadeia de clonagem (card "Cotação/Proposta de origem"). */
export type GroupLifeCloneOrigin = {
  kind: 'QUOTE' | 'PROPOSAL';
  policyId: string;
  proposalNumber: string;
  stipulatorLegalName?: string;
  clonedAt: string;
};

/**
 * Step de pagamento revertido do InsureMO. O back deriva tudo das parcelas do
 * `PolicyPaymentInfoList` (dia de vencimento = dia das parcelas; período de
 * corte = mês da 1ª), então não há campo novo no produto.
 */
export type GroupLifeFrontPayment = {
  billingFrequency: string;
  paymentMethod: string;
  billingCut: 'postecipado' | 'durante-o-mes' | '';
  /** Dia de vencimento como string ('1'..'31'). */
  dueDay: string;
};

export type GroupLifeQuoteDetails = {
  policyId: string;
  proposalNumber: string;
  productCode: string;
  /** StatusPortal do InsureMO (ex.: 3 = calculada, 17 = em análise). */
  statusPortal?: string;
  /** Motivo informado pela subscrição ao recusar a cotação. */
  rejectionReason?: string;
  stipulator?: GroupLifeFrontStipulator;
  plan?: GroupLifeFrontPlan;
  subStipulators: GroupLifeFrontSubStipulator[];
  commercialization?: GroupLifeFrontCommercialization;
  stipulatorMembers?: GroupLifeFrontMember[];
  /** Resultado do cálculo (prêmio) — presente após o Step 4. */
  calculation?: GroupLifeQuoteCalculation;
  effectiveDate?: string;
  /** Pagamento/faturamento do step 3 — ausente antes do primeiro cálculo. */
  payment?: GroupLifeFrontPayment;
  /**
   * Validade da cotação/proposta em DD/MM/YYYY (StatusExpirationDate →
   * ExpiryDate). Existe mesmo sem cálculo — ex.: proposta clonada antes do
   * primeiro recálculo, quando `calculation.validity` ainda não existe.
   */
  validity?: string;
  /**
   * Cadeia de ancestrais quando esta cotação/proposta é um clone — ordenada
   * do pai imediato à raiz (clone de clone lista todos até a original).
   */
  clonedFrom?: GroupLifeCloneOrigin[];
  /**
   * Etapas da proposta que o corretor já GRAVOU (`insurance`, `plan`,
   * `payment`). Vigência e pagamento existem desde a cotação, então só isto
   * diz se o corretor passou por elas — o Confirmar da Api cobra o mesmo.
   */
  completedSteps?: string[];
};

/**
 * Resposta do save de comercialização na análise (cotação e proposta).
 * Campos opcionais: uma API anterior a esta mudança responde sem eles e o
 * front cai no caminho conservador (invalidar todos os caches).
 */
export type GroupLifeCalculateAnalysisResult = {
  /**
   * `unchanged`: os percentuais já estavam salvos — nada gravado nem
   * recalculado. `processing`: a comercialização foi gravada e o prêmio
   * ainda está sendo recalculado. `recalculated`: o prêmio novo já veio
   * na resposta.
   */
  outcome?: 'unchanged' | 'processing' | 'recalculated';
  /** Snapshot já atualizado (sem vidas) — evita um GET de detalhe extra. */
  details?: GroupLifeQuoteDetails;
  totalPremium?: number;
  sentToUnderwritingAnalysis?: boolean;
};

/** Cobertura de uma vida na Tarifação (decodificada do GroupPolicyCoverageData). */
/** Linha da coluna FATORES/TAXA (composição descritiva + valor do fator). */
export type GroupLifeTarifacaoCoverageFactor = {
  /** Ex.: `Morte - FEMININO - 49` ou `Morte - DESCONTO - CNAE: 8299799`. */
  label: string;
  value: number | null;
};

export type GroupLifeTarifacaoCoverage = {
  /** Código da cobertura no produto (ex.: `9300002`). */
  code: string;
  /** Nome resolvido pelo productSchema; cai para o código quando ausente. */
  name: string;
  /** FATORES/TAXA — 1 linha (sexo/idade) + 1 opcional (desconto CNAE). */
  factors: GroupLifeTarifacaoCoverageFactor[];
  sumInsured: number | null;
  /** Taxa net (`NetRate`) — precisão da origem, sem arredondamento. */
  netRate: number | null;
  /** Taxa comercial (`ComercialRate`) — precisão da origem. */
  commercialRate: number | null;
  /** Taxa pura (`PureRate`) — usada na taxa média por cobertura. */
  pureRate: number | null;
  purePremium: number | null;
  netPremium: number | null;
  grossPremium: number | null;
  duePremium: number | null;
  iof: number | null;
};

export type GroupLifeTarifacaoLife = {
  policyElementId: string;
  name: string;
  /** CPF da vida (pode vir mascarado) — usado no filtro por CPF. */
  cpf: string;
  gender: 'M' | 'F' | '';
  /** `YYYY-MM-DD` como veio do InsureMO. */
  birthDate: string;
  /** Código do grupo segurável (ex.: `EMPLOYEES`) — vazio quando não lido. */
  insurableGroup: string;
  /** Capital/salário base já formatado em BRL — vazio quando não lido. */
  capital: string;
  riskGroupNo: number | null;
  /** `false` = vida herda as coberturas do Risk Group (sem override). */
  hasOwnCoverages: boolean;
  coverages: GroupLifeTarifacaoCoverage[];
  totalNetPremium: number;
  totalIof: number;
  totalDuePremium: number;
};

/** Proponente/Corretor do cabeçalho da Tarifação (página 1 do PDF original). */
export type GroupLifeTarifacaoParties = {
  /** Nº da proposta/cotação no InsureMO (`ProposalNo`). */
  proposalNumber?: string;
  proponente: {
    legalName: string;
    /** 'Jurídica' (CNPJ) ou 'CAEPF'. */
    type: string;
    document: string;
    /** `código — descrição` quando resolvida; senão só o código. */
    mainActivity?: string;
  };
  broker?: {
    code?: string;
    name?: string;
    susep?: string;
    branch?: string;
  };
};

/** Percentuais de comercialização do snapshot. `null` = não informado. */
export type GroupLifeTarifacaoCommercialization = {
  brokeragePercent: number | null;
  /** Nome atual exposto pelo snapshot PME. */
  agencingPercent?: number | null;
  /** Alias legado do endpoint agregador de tarifação. */
  agencyPercent: number | null;
  surchargePercent: number | null;
  actuarialDiscountPercent: number | null;
};

/** Prêmio da apólice (KPIs) — não muda com filtro/paginação. */
export type GroupLifeTarifacaoPremium = {
  total: number | null;
  net: number | null;
  iof: number | null;
};

export type GroupLifeTarifacaoPage = {
  /** Cabeçalho (Proponente/Corretor) — ausente se o snapshot falhar. */
  parties?: GroupLifeTarifacaoParties;
  /** Carregamento (corretagem/agenciamento/agravo/desconto) do snapshot. */
  commercialization?: GroupLifeTarifacaoCommercialization;
  /** Prêmio consolidado da apólice (KPIs). */
  premium?: GroupLifeTarifacaoPremium;
  /** Total de vidas da apólice (sem filtro). */
  livesTotal?: number;
  /** `false` = apólice sem vidas destacadas (coberturas moram no snapshot). */
  detached: boolean;
  /** `true` = proposta ainda não calculada (prêmios zerados). */
  notCalculated: boolean;
  data: GroupLifeTarifacaoLife[];
  meta: {
    pageNumber: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
};

export class GroupLifeService extends BaseService {
  /** Baixa o template oficial de vidas (.xlsx). O backend transmite o arquivo
   *  diretamente (asset local, sem S3): buscamos o blob e disparamos o download
   *  no navegador via Object URL. */
  async downloadMembersTemplate(): Promise<{
    success: boolean;
    error?: string;
  }> {
    try {
      const path = '/portal/v1/group-life/members/template';
      const response = await this.get(path, {}, import.meta.env.VITE_API_URL);
      if (!response.ok) {
        return {
          success: false,
          error: `Falha ao baixar template (${response.status})`,
        };
      }
      const blob = await response.blob();
      // Nome do arquivo vem do Content-Disposition; fallback para um padrão.
      const disposition = response.headers.get('content-disposition') ?? '';
      const match = disposition.match(/filename="?([^"]+)"?/i);
      const filename = match?.[1] ?? 'PME-planilha-de-vidas.xlsx';
      const objectUrl = URL.createObjectURL(blob);
      this.triggerBrowserDownload(objectUrl, filename);
      // Revoga depois de o navegador iniciar o download.
      setTimeout(() => URL.revokeObjectURL(objectUrl), 10_000);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro desconhecido',
      };
    }
  }

  /** Gera (no back) e retorna a URL pré-assinada do .xlsx com as vidas de um
   *  target (estipulante master ou subestipulante). */
  async getMembersExport(params: {
    quoteId: string;
    targetId: string;
    /** Rótulo amigável (razão social) para compor o nome do arquivo. */
    filename?: string;
  }): Promise<{
    success: boolean;
    data?: GroupLifeMembersExport;
    error?: string;
    status?: number;
  }> {
    try {
      const qs = new URLSearchParams({ targetId: params.targetId });
      if (params.filename) qs.set('filename', params.filename);
      const path = `/portal/v1/group-life/quotes/${encodeURIComponent(
        params.quoteId
      )}/members/export?${qs.toString()}`;
      const response = await this.get(path, {}, import.meta.env.VITE_API_URL);
      const result =
        await this.handleJsonResponse<GroupLifeMembersExport>(response);
      return { ...result, status: response.status };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro desconhecido',
      };
    }
  }

  /** Exporta as vidas de um target e dispara o download no browser. */
  async downloadMembersExport(params: {
    quoteId: string;
    targetId: string;
    filename?: string;
  }): Promise<{ success: boolean; total?: number; error?: string }> {
    const result = await this.getMembersExport(params);
    if (!result.success || !result.data) {
      return {
        success: false,
        error: result.error ?? 'Falha ao exportar as vidas',
      };
    }
    this.triggerBrowserDownload(result.data.url, result.data.filename);
    return { success: true, total: result.data.total };
  }

  /** Cria um link temporário e dispara o download de uma URL. */
  private triggerBrowserDownload(url: string, filename: string): void {
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  /** Uploads the Excel members sheet for a given quote and target
   *  (Estipulante or SubEstipulante). The backend persists it to S3 and
   *  queues processing — actual parsing/InsureMO sync happen in the next
   *  phase. */
  async uploadMembersSheet(params: {
    quoteId: string;
    targetId: string;
    file: File;
    UniqueID: string;
  }): Promise<{
    success: boolean;
    data?: GroupLifeMembersImportResponse;
    error?: string;
    status?: number;
  }> {
    try {
      const baseUrl = import.meta.env.VITE_API_URL || this.baseUrl;
      const path = `/portal/v1/group-life/quotes/${encodeURIComponent(
        params.quoteId
      )}/members/import/${encodeURIComponent(params.targetId)}`;

      const form = new FormData();
      form.append('file', params.file, params.file.name);
      form.append('UniqueID', params.UniqueID);

      const accessToken = await authenticationService.getAuthSession();
      const headers: Record<string, string> = {};
      if (accessToken) {
        headers['Authorization'] = `Bearer ${accessToken.toString()}`;
      }

      const response = await fetch(`${baseUrl}${path}`, {
        method: 'POST',
        body: form,
        headers,
      });

      const result =
        await this.handleJsonResponse<GroupLifeMembersImportResponse>(response);
      return { ...result, status: response.status };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro desconhecido',
      };
    }
  }

  /** Upload ASSÍNCRONO da planilha de vidas via Upload Framework do InsureMO.
   *  Resposta 202 imediata com `importId`/`taskId`/`totalExpected` + preview
   *  dos members do parsing local. O front continua consultando o status via
   *  `getAsyncImportStatus` até `COMPLETED`/`FAILED`. */
  async uploadMembersSheetAsync(params: {
    quoteId: string;
    targetId: string;
    file: File;
    UniqueID?: string;
    /** Usa o endpoint de importação de vidas da proposta. */
    isProposal?: boolean;
    /** Contexto do plano (modalidade não é persistida no back) — usado para
     *  aplicar as regras de capital por modalidade na importação. */
    plan?: {
      modality?: string;
      uniformCapital?: string;
      numberOfSalaries?: string;
      minimumIndividualCapital?: string;
      maximumIndividualCapital?: string;
    };
  }): Promise<{
    success: boolean;
    data?: GroupLifeAsyncImportStartResponse;
    error?: string;
    status?: number;
    /** Quando 422 vem com `code === GROUP_LIFE_DUPLICATE_CPF`, o back devolve
     *  esta estrutura agrupada — usada pelo Step 3 para abrir o modal de
     *  duplicatas em vez de um alert simples. */
    duplicates?: GroupLifeDuplicateCpfsPayload;
  }> {
    try {
      const baseUrl = import.meta.env.VITE_API_URL || this.baseUrl;
      const resource = params.isProposal ? 'proposals' : 'quotes';
      const path = `/portal/v1/group-life/${resource}/${encodeURIComponent(
        params.quoteId
      )}/members/import-async/${encodeURIComponent(params.targetId)}`;

      const form = new FormData();
      form.append('file', params.file, params.file.name);
      if (params.UniqueID) form.append('UniqueID', params.UniqueID);
      // Contexto do plano (regras de capital por modalidade na importação).
      if (params.plan?.modality) form.append('modality', params.plan.modality);
      if (params.plan?.uniformCapital)
        form.append('uniformCapital', params.plan.uniformCapital);
      if (params.plan?.numberOfSalaries)
        form.append('numberOfSalaries', params.plan.numberOfSalaries);
      if (params.plan?.minimumIndividualCapital)
        form.append(
          'minimumIndividualCapital',
          params.plan.minimumIndividualCapital
        );
      if (params.plan?.maximumIndividualCapital)
        form.append(
          'maximumIndividualCapital',
          params.plan.maximumIndividualCapital
        );

      const accessToken = await authenticationService.getAuthSession();
      const headers: Record<string, string> = {};
      if (accessToken) {
        headers['Authorization'] = `Bearer ${accessToken.toString()}`;
      }

      const response = await fetch(`${baseUrl}${path}`, {
        method: 'POST',
        body: form,
        headers,
      });

      // Caminho específico para 422 com duplicatas: lemos o body uma vez e
      // extraímos `code` + `duplicates` (extensões do ProblemDetails). Cai
      // de volta no `handleJsonResponse` se não for o nosso shape.
      if (response.status === 422) {
        try {
          const body = (await response.clone().json()) as {
            detail?: string;
            code?: string;
            duplicates?: GroupLifeDuplicateCpfsPayload;
          };
          if (body?.code === 'GROUP_LIFE_DUPLICATE_CPF' && body.duplicates) {
            return {
              success: false,
              status: response.status,
              error: body.detail,
              duplicates: body.duplicates,
            };
          }
        } catch {
          /* body não JSON — segue fluxo padrão */
        }
      }

      const result =
        await this.handleJsonResponse<GroupLifeAsyncImportStartResponse>(
          response
        );
      return { ...result, status: response.status };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro desconhecido',
      };
    }
  }

  /** Consulta o status do job assíncrono. Polling até `status ∈
   *  {COMPLETED, FAILED}`. Quando completed, devolve as vidas materializadas
   *  com `PolicyElementId` no campo `id`. */
  async getAsyncImportStatus(params: {
    policyId: string;
    importId: string;
    isProposal?: boolean;
  }): Promise<{
    success: boolean;
    data?: GroupLifeAsyncImportStatusResponse;
    error?: string;
    status?: number;
  }> {
    try {
      const resource = params.isProposal ? 'proposals' : 'quotes';
      const path = `/portal/v1/group-life/${resource}/${encodeURIComponent(
        params.policyId
      )}/members/import-async/${encodeURIComponent(params.importId)}/status`;
      const response = await this.get(path, {}, import.meta.env.VITE_API_URL);
      const result =
        await this.handleJsonResponse<GroupLifeAsyncImportStatusResponse>(
          response
        );
      return { ...result, status: response.status };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro desconhecido',
      };
    }
  }

  /** Adiciona UMA vida na cotação — persiste imediatamente no InsureMO via
   *  endpoints detached + store durável. Devolve a vida materializada com o
   *  PolicyElementId que vira o `id` no grid. */
  async addMember(params: {
    quoteId: string;
    input: GroupLifeMemberCrudInput;
  }): Promise<{
    success: boolean;
    data?: GroupLifeMemberCrudResponse;
    error?: string;
    status?: number;
    duplicateCpf?: boolean;
  }> {
    try {
      const path = `/portal/v1/group-life/quotes/${encodeURIComponent(
        params.quoteId
      )}/members`;
      const response = await this.post(
        path,
        params.input,
        {},
        import.meta.env.VITE_API_URL
      );
      if (response.status === 422) {
        try {
          const body = (await response.clone().json()) as {
            detail?: string;
            code?: string;
          };
          if (body?.code === 'GROUP_LIFE_DUPLICATE_CPF') {
            return {
              success: false,
              status: response.status,
              error: body.detail,
              duplicateCpf: true,
            };
          }
        } catch {
          /* body não JSON — segue fluxo padrão */
        }
      }
      const result =
        await this.handleJsonResponse<GroupLifeMemberCrudResponse>(response);
      return { ...result, status: response.status };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro desconhecido',
      };
    }
  }

  /** Atualiza UMA vida existente identificada pelo memberId (= PolicyElementId). */
  async updateMember(params: {
    quoteId: string;
    memberId: string;
    input: GroupLifeMemberCrudInput;
  }): Promise<{
    success: boolean;
    data?: GroupLifeMemberCrudResponse;
    error?: string;
    status?: number;
    duplicateCpf?: boolean;
  }> {
    try {
      const path = `/portal/v1/group-life/quotes/${encodeURIComponent(
        params.quoteId
      )}/members/${encodeURIComponent(params.memberId)}`;
      const response = await this.put(
        path,
        params.input,
        {},
        import.meta.env.VITE_API_URL
      );
      if (response.status === 422) {
        try {
          const body = (await response.clone().json()) as {
            detail?: string;
            code?: string;
          };
          if (body?.code === 'GROUP_LIFE_DUPLICATE_CPF') {
            return {
              success: false,
              status: response.status,
              error: body.detail,
              duplicateCpf: true,
            };
          }
        } catch {
          /* body não JSON — segue fluxo padrão */
        }
      }
      const result =
        await this.handleJsonResponse<GroupLifeMemberCrudResponse>(response);
      return { ...result, status: response.status };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro desconhecido',
      };
    }
  }

  /** Remove UMA vida da cotação. */
  async deleteMember(params: { quoteId: string; memberId: string }): Promise<{
    success: boolean;
    data?: { deletedId: string };
    error?: string;
    status?: number;
  }> {
    try {
      const path = `/portal/v1/group-life/quotes/${encodeURIComponent(
        params.quoteId
      )}/members/${encodeURIComponent(params.memberId)}`;
      const response = await this.delete(
        path,
        {},
        import.meta.env.VITE_API_URL
      );
      const result = await this.handleJsonResponse<{ deletedId: string }>(
        response
      );
      return { ...result, status: response.status };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro desconhecido',
      };
    }
  }

  // ── CRUD de SUBESTIPULANTES (PolicyRiskGroup) ────────────────────────
  // Persistência imediata no InsureMO via updateEx; na remoção, cascade
  // delete das vidas atreladas.

  async addSubStipulator(params: {
    quoteId: string;
    input: GroupLifeSubStipulatorCrudInput;
  }): Promise<{
    success: boolean;
    data?: GroupLifeSubStipulatorResponse;
    error?: string;
    status?: number;
  }> {
    try {
      const path = `/portal/v1/group-life/quotes/${encodeURIComponent(
        params.quoteId
      )}/sub-stipulators`;
      const response = await this.post(
        path,
        params.input,
        {},
        import.meta.env.VITE_API_URL
      );
      const result =
        await this.handleJsonResponse<GroupLifeSubStipulatorResponse>(response);
      return { ...result, status: response.status };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro desconhecido',
      };
    }
  }

  async updateSubStipulator(params: {
    quoteId: string;
    subId: string;
    input: GroupLifeSubStipulatorCrudInput;
  }): Promise<{
    success: boolean;
    data?: GroupLifeSubStipulatorResponse;
    error?: string;
    status?: number;
  }> {
    try {
      const path = `/portal/v1/group-life/quotes/${encodeURIComponent(
        params.quoteId
      )}/sub-stipulators/${encodeURIComponent(params.subId)}`;
      const response = await this.put(
        path,
        params.input,
        {},
        import.meta.env.VITE_API_URL
      );
      const result =
        await this.handleJsonResponse<GroupLifeSubStipulatorResponse>(response);
      return { ...result, status: response.status };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro desconhecido',
      };
    }
  }

  async deleteSubStipulator(params: {
    quoteId: string;
    subId: string;
  }): Promise<{
    success: boolean;
    data?: GroupLifeSubStipulatorDeleted;
    error?: string;
    status?: number;
  }> {
    try {
      const path = `/portal/v1/group-life/quotes/${encodeURIComponent(
        params.quoteId
      )}/sub-stipulators/${encodeURIComponent(params.subId)}`;
      const response = await this.delete(
        path,
        {},
        import.meta.env.VITE_API_URL
      );
      const result =
        await this.handleJsonResponse<GroupLifeSubStipulatorDeleted>(response);
      return { ...result, status: response.status };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro desconhecido',
      };
    }
  }

  /**
   * Lista as vidas de UM target (master ou subestipulante). Usado pelo
   * Step 3 para carregar os grids em requests separados — evita um único
   * response gigante de 5k+ vidas.
   */
  async listMembersByTarget(params: {
    quoteId: string;
    targetId: string;
  }): Promise<{
    success: boolean;
    data?: GroupLifeMemberCrudResponse[];
    error?: string;
    status?: number;
  }> {
    try {
      const search = new URLSearchParams({ targetId: params.targetId });
      const path = `/portal/v1/group-life/quotes/${encodeURIComponent(
        params.quoteId
      )}/members?${search.toString()}`;
      const response = await this.get(
        path,
        { cache: 'no-store' },
        import.meta.env.VITE_API_URL
      );
      const result =
        await this.handleJsonResponse<GroupLifeMemberCrudResponse[]>(response);
      return { ...result, status: response.status };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro desconhecido',
      };
    }
  }

  /** Loads the full snapshot of a Group Life quote from InsureMO and maps
   *  it back to the front-side shape — used to hydrate `useGroupLifeQuote`
   *  when the user refreshes the page. */
  async getQuoteDetails(policyId: string): Promise<{
    success: boolean;
    data?: GroupLifeQuoteDetails;
    error?: string;
    status?: number;
  }> {
    try {
      const path = `/portal/v1/group-life/quotes/${encodeURIComponent(policyId)}`;
      const response = await this.get(
        path,
        { cache: 'no-store' },
        import.meta.env.VITE_API_URL
      );
      const result =
        await this.handleJsonResponse<GroupLifeQuoteDetails>(response);
      return { ...result, status: response.status };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro desconhecido',
      };
    }
  }

  async listQuoteVersions(policyId: string): Promise<{
    success: boolean;
    data?: ListGroupLifeQuoteVersionsResponse;
    error?: string;
    status?: number;
  }> {
    try {
      const path = `/portal/v1/group-life/quotes/${encodeURIComponent(
        policyId
      )}/versions`;
      const response = await this.get(path, {}, import.meta.env.VITE_API_URL);
      const result =
        await this.handleJsonResponse<ListGroupLifeQuoteVersionsResponse>(
          response
        );
      return { ...result, status: response.status };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : 'Erro ao listar versões da cotação',
      };
    }
  }

  async getQuoteAtVersion(
    policyId: string,
    quotationVersionId: string | number
  ): Promise<{
    success: boolean;
    data?: GroupLifeQuoteDetails;
    error?: string;
    status?: number;
  }> {
    try {
      const path = `/portal/v1/group-life/quotes/${encodeURIComponent(
        policyId
      )}/versions/${encodeURIComponent(String(quotationVersionId))}`;
      const response = await this.get(path, {}, import.meta.env.VITE_API_URL);
      const result =
        await this.handleJsonResponse<GroupLifeQuoteDetails>(response);
      return { ...result, status: response.status };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : 'Erro ao carregar a versão da cotação',
      };
    }
  }

  /**
   * Gera o PDF da cotação. O mapping do template é montado pelo back-end a
   * partir do policyId — o front não envia nenhum dado do documento.
   */
  async generateQuotePdf(params: { policyId: string }): Promise<{
    success: boolean;
    data?: GroupLifeQuotePdfResponse;
    error?: string;
    status?: number;
  }> {
    try {
      const path = `/portal/v1/group-life/quotes/${encodeURIComponent(
        params.policyId
      )}/pdf`;
      const response = await this.post(path, {}, import.meta.env.VITE_API_URL);
      const result =
        await this.handleJsonResponse<GroupLifeQuotePdfResponse>(response);
      return { ...result, status: response.status };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao gerar PDF',
      };
    }
  }

  /** Creates the InsureMO draft proposal from step-1 (Estipulante) data. */
  async createQuote(payload: GroupLifeCreateQuotePayload): Promise<{
    success: boolean;
    data?: GroupLifeCreateQuoteResponse;
    error?: string;
    status?: number;
  }> {
    try {
      const path = '/portal/v1/group-life/quotes';
      const response = await this.post(
        path,
        payload,
        import.meta.env.VITE_API_URL
      );
      const result =
        await this.handleJsonResponse<GroupLifeCreateQuoteResponse>(response);
      return { ...result, status: response.status };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro desconhecido',
      };
    }
  }

  /**
   * @param idempotencyKey repetida em toda tentativa da mesma clonagem: a API
   * devolve o clone já criado (ou 409 enquanto ele roda) em vez de criar outro.
   */
  async copyQuote(
    policyId: string,
    idempotencyKey?: string
  ): Promise<{
    success: boolean;
    data?: GroupLifeCopyQuoteResponse;
    error?: string;
    status?: number;
  }> {
    try {
      const path = `/portal/v1/group-life/quotes/${encodeURIComponent(
        policyId
      )}/copy`;
      const response = await this.post(
        path,
        undefined,
        idempotencyKey
          ? { headers: { 'Idempotency-Key': idempotencyKey } }
          : {},
        import.meta.env.VITE_API_URL
      );
      const result =
        await this.handleJsonResponse<GroupLifeCopyQuoteResponse>(response);
      return { ...result, status: response.status };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error ? error.message : 'Erro ao clonar cotação',
      };
    }
  }

  async getMembersRatingProgress(policyId: string): Promise<{
    success: boolean;
    data?: GroupLifeMembersRatingProgress;
    error?: string;
    status?: number;
  }> {
    try {
      const path = `/portal/v1/group-life/quotes/${encodeURIComponent(
        policyId
      )}/members-rating-progress`;
      const response = await this.get(path, import.meta.env.VITE_API_URL);
      const result =
        await this.handleJsonResponse<GroupLifeMembersRatingProgress>(
          response
        );
      return { ...result, status: response.status };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : 'Erro ao consultar o progresso da re-tarifação',
      };
    }
  }

  async getDailyBenefitRate(params: {
    productCode: string;
    coverageCode: string;
    benefitCode: string;
  }): Promise<{
    success: boolean;
    data?: { maxDailyRate: number };
    error?: string;
    status?: number;
  }> {
    try {
      const query = new URLSearchParams({
        productCode: params.productCode,
        coverageCode: params.coverageCode,
        benefitCode: params.benefitCode,
      });
      const path = `/portal/v1/group-life/quotes/daily-benefit-rate?${query.toString()}`;
      const response = await this.get(path, import.meta.env.VITE_API_URL);
      const result = await this.handleJsonResponse<{ maxDailyRate: number }>(
        response
      );
      return { ...result, status: response.status };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : 'Erro ao consultar o teto da diária',
      };
    }
  }

  async calculateAnalysis(
    policyId: string,
    payload: GroupLifeCalculateAnalysisPayload
  ): Promise<{
    success: boolean;
    data?: GroupLifeCalculateAnalysisResult;
    error?: string;
    status?: number;
  }> {
    try {
      const path = `/portal/v1/group-life/quotes/${encodeURIComponent(
        policyId
      )}/calculate-analysis`;
      const response = await this.post(
        path,
        payload,
        import.meta.env.VITE_API_URL
      );
      const result =
        await this.handleJsonResponse<GroupLifeCalculateAnalysisResult>(
          response
        );
      return { ...result, status: response.status };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : 'Erro ao recalcular cotação em análise',
      };
    }
  }

  async submitAnalysisCalculation(policyId: string): Promise<{
    success: boolean;
    data?: GroupLifeSubmitAnalysisResponse;
    error?: string;
    status?: number;
  }> {
    try {
      const path = `/portal/v1/group-life/quotes/${encodeURIComponent(
        policyId
      )}/submit-analysis-calculation`;
      const response = await this.post(
        path,
        undefined,
        import.meta.env.VITE_API_URL
      );
      const result =
        await this.handleJsonResponse<GroupLifeSubmitAnalysisResponse>(
          response
        );
      return { ...result, status: response.status };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : 'Erro ao aprovar cotação em análise',
      };
    }
  }

  async rejectAnalysis(
    policyId: string,
    description: string
  ): Promise<{
    success: boolean;
    data?: GroupLifeRejectAnalysisResponse;
    error?: string;
    status?: number;
  }> {
    try {
      const path = `/portal/v1/group-life/quotes/${encodeURIComponent(
        policyId
      )}/reject-analysis`;
      const response = await this.post(
        path,
        { description },
        import.meta.env.VITE_API_URL
      );
      const result =
        await this.handleJsonResponse<GroupLifeRejectAnalysisResponse>(
          response
        );
      return { ...result, status: response.status };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : 'Erro ao recusar cotação em análise',
      };
    }
  }

  /** Persists a specific wizard step into InsureMO (updateEx under the hood). */
  async saveStep(params: {
    policyId: string;
    step: GroupLifeStepName;
    payload: Record<string, unknown>;
  }): Promise<{
    success: boolean;
    data?: GroupLifeSaveStepResponse;
    error?: string;
    status?: number;
  }> {
    try {
      const baseUrl = import.meta.env.VITE_API_URL || this.baseUrl;
      const path = `/portal/v1/group-life/quotes/${encodeURIComponent(
        params.policyId
      )}/step/${encodeURIComponent(params.step)}`;

      const accessToken = await authenticationService.getAuthSession();
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      if (accessToken) {
        headers['Authorization'] = `Bearer ${accessToken.toString()}`;
      }
      const response = await fetch(`${baseUrl}${path}`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify({ payload: params.payload }),
      });
      const result =
        await this.handleJsonResponse<GroupLifeSaveStepResponse>(response);
      return { ...result, status: response.status };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro desconhecido',
      };
    }
  }

  async calculateQuote(policyId: string): Promise<{
    success: boolean;
    data?: Record<string, unknown>;
    error?: string;
    status?: number;
    sentToUnderwritingAnalysis?: boolean;
    underwritingMessages?: BusinessUnderwritingMessage[];
  }> {
    try {
      const path = `/portal/v1/group-life/quotes/${encodeURIComponent(
        policyId
      )}/calculate`;
      const response = await this.post(
        path,
        undefined,
        import.meta.env.VITE_API_URL
      );

      if (response.status === 400) {
        const raw = await response.text();
        const body = raw
          ? ((): {
              detail?: string;
              message?: string;
              underwritingMessages?: BusinessUnderwritingMessage[];
            } | null => {
              try {
                return JSON.parse(raw);
              } catch {
                return null;
              }
            })()
          : null;

        return {
          success: false,
          error:
            body?.detail ||
            body?.message ||
            `Erro ${response.status}: ${response.statusText}`,
          status: response.status,
          underwritingMessages: body?.underwritingMessages,
        };
      }

      const result =
        await this.handleJsonResponse<Record<string, unknown>>(response);
      if (!result.success || !result.data) {
        return result;
      }

      const sentToUnderwritingAnalysis =
        result.data.sentToUnderwritingAnalysis === true;
      const underwritingMessages = Array.isArray(
        result.data.underwritingMessages
      )
        ? (result.data.underwritingMessages as BusinessUnderwritingMessage[])
        : undefined;

      return {
        ...result,
        status: response.status,
        sentToUnderwritingAnalysis,
        underwritingMessages,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro desconhecido',
      };
    }
  }

  async getUnderwritingMessages(policyId: string): Promise<{
    success: boolean;
    data?: BusinessUnderwritingMessagesResponse;
    error?: string;
  }> {
    try {
      const path = `/portal/v1/group-life/quotes/${encodeURIComponent(
        policyId
      )}/underwriting-messages`;
      const response = await this.get(path, {}, import.meta.env.VITE_API_URL);
      return await this.handleJsonResponse<BusinessUnderwritingMessagesResponse>(
        response
      );
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : 'Erro ao carregar mensagens de subscrição',
      };
    }
  }

  /**
   * URL pré-assinada do PDF de análise de crédito do estipulante (WI 2224).
   *
   * Restrito a perfil da seguradora: o backend responde 403 para corretor, e o
   * relatório NÃO é servido pela rota genérica de documentos da cotação.
   */
  async getCreditAnalysisPdf(policyId: string): Promise<{
    success: boolean;
    data?: { key: string; url: string; fileName?: string };
    error?: string;
  }> {
    try {
      const path = `/portal/v1/group-life/quotes/${encodeURIComponent(
        policyId
      )}/credit-analysis/pdf`;
      const response = await this.get(path, {}, import.meta.env.VITE_API_URL);
      return await this.handleJsonResponse<{
        key: string;
        url: string;
        fileName?: string;
      }>(response);
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : 'Erro ao gerar o relatório de crédito',
      };
    }
  }

  /** Fetches the InsureMO product schema for Group Life — the source of
   *  truth that drives Step 2 rendering. */
  async getCoveragesSchema(params: {
    productCode: string;
    versionDate?: string;
  }): Promise<{
    success: boolean;
    data?: CoverageSchemaResponse;
    error?: string;
    status?: number;
  }> {
    try {
      const search = new URLSearchParams({ productCode: params.productCode });
      if (params.versionDate) search.set('versionDate', params.versionDate);
      const path = `/portal/v1/group-life/coverages/schema?${search.toString()}`;
      const response = await this.get(path, {}, import.meta.env.VITE_API_URL);
      const result =
        await this.handleJsonResponse<CoverageSchemaResponse>(response);
      return { ...result, status: response.status };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro desconhecido',
      };
    }
  }

  /**
   * Lista uma PÁGINA de vidas de um target (server-side) com contagem total
   * e busca. Escala para 5000+ vidas (não carrega o conjunto inteiro) e
   * alcança qualquer página, mesmo além de 1000 vidas.
   */
  async listMembersPage(params: {
    quoteId: string;
    targetId: string;
    pageNo: number;
    pageSize: number;
    search?: string;
  }): Promise<{
    success: boolean;
    data?: GroupLifeMembersPageResponse;
    error?: string;
    status?: number;
  }> {
    try {
      const qs = new URLSearchParams({
        targetId: params.targetId,
        pageNo: String(params.pageNo),
        pageSize: String(params.pageSize),
      });
      if (params.search) qs.set('search', params.search);
      const path = `/portal/v1/group-life/quotes/${encodeURIComponent(
        params.quoteId
      )}/members/page?${qs.toString()}`;
      const response = await this.get(path, {}, import.meta.env.VITE_API_URL);
      const result =
        await this.handleJsonResponse<GroupLifeMembersPageResponse>(response);
      return { ...result, status: response.status };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro desconhecido',
      };
    }
  }

  /**
   * Página da Tarifação por vida do PME: vidas destacadas com capital/prêmios
   * por cobertura decodificados do GroupPolicyCoverageData pelo backend.
   * Alimenta o infinite scroll da tela de Tarifação — tanto da Análise de
   * Proposta (`context: 'proposal'`) quanto da Análise de Cotação
   * (`context: 'quote'`); os endpoints são espelhados.
   */
  async getTarifacaoPage(params: {
    policyId: string;
    context: 'proposal' | 'quote';
    pageNo: number;
    pageSize: number;
    /** LIKE por nome (fuzzy `FullName`) aplicado server-side. */
    search?: string;
    /**
     * Bucket das vidas: `__STIPULATOR__` = estipulante master, id do sub = só
     * as vidas dele. Ausente → apólice inteira (relatório de Tarifação).
     */
    targetId?: string;
  }): Promise<{
    success: boolean;
    data?: GroupLifeTarifacaoPage;
    error?: string;
    status?: number;
  }> {
    try {
      const qs = new URLSearchParams({
        pageNo: String(params.pageNo),
        pageSize: String(params.pageSize),
      });
      if (params.search) qs.set('search', params.search);
      if (params.targetId) qs.set('targetId', params.targetId);
      const resource = params.context === 'quote' ? 'quotes' : 'proposals';
      const path = `/portal/v1/group-life/${resource}/${encodeURIComponent(
        params.policyId
      )}/pricing?${qs.toString()}`;
      const response = await this.get(
        path,
        { cache: 'no-store' },
        import.meta.env.VITE_API_URL
      );
      const result =
        await this.handleJsonResponse<GroupLifeTarifacaoPage>(response);
      return { ...result, status: response.status };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro desconhecido',
      };
    }
  }
}

export const groupLifeService = new GroupLifeService();
